// Labels and number formatting of a goal's progress source (docs/tasks/28-goal-progress-source.md). The rules live on
// the server (zofino-strapi src/goals/progress-rules.ts).

export const SOURCES = [
    {value: "subgoals", label: "زیر اهداف", sub: "خودکار", icon: "account_tree",
        text: "پیشرفت این هدف به‌طور خودکار از میانگین پیشرفت اهدافی که زیر آن هستند به‌دست می‌آید."},
    {value: "projects", label: "پروژه‌ها", sub: "خودکار", icon: "folder_open",
        text: "پیشرفت این هدف به‌طور خودکار از پروژه‌های متصل به آن محاسبه می‌شود."},
    {value: "manual", label: "دستی", sub: "مقدار را خودتان ثبت می‌کنید", icon: "edit_note",
        text: "پیشرفت این هدف با مقداری که خودتان ثبت می‌کنید سنجیده می‌شود؛ برای نتایجی که بیرون از زوفینو اتفاق می‌افتند، مثل جذب مشتری یا فروش."},
];

export const PROJECT_MEASURES = [
    {value: "tasks", label: "کارهای پروژه‌های متصل انجام شوند", icon: "task_alt", text: "سهم کارهای انجام‌شده از همه‌ی کارهای پروژه‌های متصل"},
    {value: "milestones", label: "مایلستون‌های پروژه‌های متصل تکمیل شوند", icon: "flag", text: "میانگین پیشرفت مایلستون‌های همه‌ی پروژه‌های متصل (هر مایلستون با کارهای خودش)"},
    {value: "progress", label: "پیشرفت کلی پروژه‌های متصل", icon: "donut_large", text: "میانگین پیشرفت هر پروژه، همان عددی که در صفحه‌ی پروژه می‌بینید"},
];

export const UNITS = [
    {value: "number", label: "عدد", example: "مثلاً ۱۰۰ مشتری"},
    {value: "percent", label: "درصد", example: "مثلاً رضایت مشتری از ۰ تا ۱۰۰٪"},
    {value: "toman", label: "تومان", example: "مثلاً فروش ۱ میلیارد تومان"},
    {value: "dollar", label: "دلار", example: "مثلاً درآمد ۵۰٬۰۰۰ دلار"},
];

export const DECIMALS = [0, 1, 2, 3, 4].map((value) => ({value, label: value === 0 ? "بدون اعشار" : `${value.toLocaleString("fa-IR")} رقم اعشار`}));

export const REMINDERS = [
    {value: "off", label: "خاموش"},
    {value: "weekly", label: "هفتگی"},
    {value: "biweekly", label: "دوهفته یک‌بار"},
    {value: "monthly", label: "ماهانه"},
    {value: "quarterly", label: "فصلی"},
];

// a value in its unit: «۱۲٬۵۰۰ تومان», «$۱۲٬۵۰۰», «۴۵٪», «۱۲۰»
export function formatValue(value, unit = "number", decimals = 0) {
    if (value === null || value === undefined || value === "" || !Number.isFinite(Number(value))) return "—";
    const text = Number(value).toLocaleString("fa-IR", {minimumFractionDigits: 0, maximumFractionDigits: decimals});
    if (unit === "percent") return `${text}٪`;
    if (unit === "toman") return `${text} تومان`;
    if (unit === "dollar") return `$${text}`;
    return text;
}

export const formatPercent = (value) => (value === null || value === undefined ? "—" : `${Number(value).toLocaleString("fa-IR", {maximumFractionDigits: 1})}٪`);

// "۱۲٬۵۰۰٫۵" / "12,500.5" → 12500.5
export function parseNumber(value) {
    if (typeof value === "number") return value;
    const text = String(value ?? "")
        .replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)))
        .replace(/[٠-٩]/g, (digit) => String("٠١٢٣٤٥٦٧٨٩".indexOf(digit)))
        .replace(/[٬,\s]/g, "")
        .replace(/٫/g, ".")
        .trim();
    return text === "" ? NaN : Number(text);
}

// the same formula as the server: from start towards target, 0–100
export function manualPercent(manual, value = manual?.current) {
    const start = Number(manual?.start);
    const target = Number(manual?.target);
    const current = Number(value);
    if (![start, target, current].every(Number.isFinite)) return null;
    if (target === start) return current === target ? 100 : 0;
    return Math.max(0, Math.min(100, Math.round(((current - start) / (target - start)) * 1000) / 10));
}

export function detailText(detail) {
    if (!detail) return "";
    const fa = (value) => Number(value || 0).toLocaleString("fa-IR");
    if (detail.kind === "tasks") return `${fa(detail.done)} از ${fa(detail.total)} کار انجام شده · ${fa(detail.projects)} پروژه`;
    if (detail.kind === "milestones") return `${fa(detail.done)} از ${fa(detail.total)} مایلستون تکمیل شده · ${fa(detail.projects)} پروژه`;
    if (detail.kind === "progress") return `میانگین ${fa(detail.counted)} از ${fa(detail.projects)} پروژه`;
    if (detail.kind === "subgoals") return `میانگین ${fa(detail.counted)} از ${fa(detail.total)} زیر هدف`;
    return "";
}

export const ERRORS = {
    "goal-progress.error.source-invalid": "منبع پیشرفت را انتخاب کنید.",
    "goal-progress.error.measure-invalid": "شیوه‌ی سنجش پروژه‌ها را انتخاب کنید.",
    "goal-progress.error.unit-invalid": "واحد سنجش معتبر نیست.",
    "goal-progress.error.decimals-invalid": "رقم اعشار باید بین ۰ تا ۴ باشد.",
    "goal-progress.error.start-required": "مقدار شروع را وارد کنید.",
    "goal-progress.error.target-required": "مقدار هدف را وارد کنید.",
    "goal-progress.error.start-equals-target": "مقدار شروع و هدف نباید یکسان باشند.",
    "goal-progress.error.percent-range": "درصد باید بین ۰ تا ۱۰۰ باشد.",
    "goal-progress.error.value-invalid": "مقدار فعلی معتبر نیست.",
    "goal-progress.error.value-required": "مقدار فعلی را وارد کنید.",
    "goal-progress.error.value-too-large": "این عدد خیلی بزرگ است.",
    "goal-progress.error.date-invalid": "تاریخ معتبر نیست.",
    "goal-progress.error.date-future": "تاریخ ثبت نمی‌تواند در آینده باشد.",
    "goal-progress.error.not-manual": "به‌روزرسانی دستی فقط برای هدف با منبع «دستی» است.",
    "goal-progress.error.permission-denied": "دسترسی تغییر این هدف را ندارید.",
    "goal-update.error.invalid-frequency": "بازه‌ی یادآوری معتبر نیست.",
};

export const errorText = (error, fallback = "ذخیره نشد. دوباره تلاش کنید.") => ERRORS[error?.response?.data?.error?.message] || fallback;
